/** Existing chat adapter over the shared history window. */
import { useTranslation } from 'react-i18next';
import { HistorySheet } from '../../components/history/HistorySheet.js';
import { useCharts } from '../../hooks/useCharts.js';
import { useAllChatThreads, useDeleteChatThread, type ThreadItem } from '../../hooks/useChatThreads.js';
import { formatShortDate } from '../../lib/format-date.js';
import { personaName, coursePersonaLabel } from '../../lib/chat-persona.js';

export interface ChatHistorySheetProps {
  currentId: string | null;
  onClose: () => void;
  onOpen: (thread: ThreadItem) => void;
  onDeleted: (id: string) => void;
}

export function ChatHistorySheet({ currentId, onClose, onOpen, onDeleted }: ChatHistorySheetProps): JSX.Element {
  const { t, i18n } = useTranslation(['home', 'common', 'me', 'course']);
  const { threads, isLoading, isError, refetch } = useAllChatThreads();
  const { charts } = useCharts();
  const del = useDeleteChatThread();
  const selfChart = charts.find(chart => chart.isSelf);
  const selfLabel = t('me:charts.relation.self');
  function nameOf(thread: ThreadItem): string {
    if (thread.surface === 'course') return coursePersonaLabel(thread.coursePersona, t);
    const chart = thread.chartId ? charts.find(chart => chart.id === thread.chartId) : selfChart;
    return chart ? personaName(chart, selfLabel) : selfLabel;
  }
  return <HistorySheet testPrefix="chat-history" rowPrefix="thread" currentId={currentId}
    rows={threads.map(thread => ({ id: thread.id, heading: `${formatShortDate(thread.lastMessageAt, i18n.language)} | ${nameOf(thread)}`, title: (thread.title ?? '').split(/\r?\n/)[0]?.trim() || t('chat.history.untitled') }))}
    loading={isLoading} error={isError} onRetry={refetch}
    labels={{ title: t('chat.history.title'), close: t('chat.history.close'), loading: t('common:states.loading'), empty: t('chat.history.empty'), loadError: t('chat.history.loadError'), retry: t('chat.retry'), delete: t('chat.history.delete'), deleteTitle: t('chat.history.deleteTitle'), deleteBody: title => t('chat.history.deleteBody', { title }), deleteConfirm: t('chat.history.deleteConfirm'), cancel: t('common:actions.cancel'), deleteError: t('chat.history.deleteError') }}
    onClose={onClose} onOpen={id => { if (id === currentId) { onClose(); return; } const thread = threads.find(thread => thread.id === id); if (thread) onOpen(thread); }} onDelete={del.mutateAsync} onDeleted={onDeleted} />;
}
